import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { offersPurchasesQuery } from "./offerAnalytics.queries";
import { filterByOffer, getPurchasePrice } from "./offerAnalytics.utils";
import {
  getOfferPurchaseHref,
  offerPurchaseColumns,
} from "./tableParams/offerPurchaseColumns";
import useAbility from "../../../hooks/useAbility";
import { getOfferDescription } from "../../../utils/data/Offers";
import ItemStatus from "../../../utils/ItemStatus";
import DataTable from "../../dataTable/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";

import type { JSX } from "react";

import type { OfferAnalyticsFilterValues } from "./offerAnalytics.types";
import type { OfferPurchaseRow } from "./tableParams/offerPurchaseColumns.types";
import type { AppState } from "../../../reducers";

interface PurchasesTableProps {
  filters: OfferAnalyticsFilterValues;
}

function PurchasesTable({ filters }: PurchasesTableProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { canAccess } = useAbility();
  const canLink = canAccess(orgaOptions).project.salesWrite;

  const {
    data: offersPurchases,
    isLoading,
    isPreviousData,
    isError,
    refetch,
  } = useQuery(offersPurchasesQuery(projectSlug, filters));

  const rowList = useMemo(
    (): OfferPurchaseRow[] =>
      filterByOffer(
        offersPurchases?.data ?? [],
        filters.offerUuid,
        (purchase) => purchase.offer_uuid,
      ).map((purchase) => ({
        offer_uuid: purchase.offer_uuid,
        status: ItemStatus.getOfferStatus(purchase.offer),
        offer_label: purchase.offer.label,
        description: getOfferDescription(t, purchase),
        type: purchase.type,
        price: getPurchasePrice(purchase),
        currency: purchase.currency,
        total_sales: purchase.total_sales,
        direct_sales: purchase.direct_sales,
        gifts: purchase.gifts,
      })),
    [offersPurchases, filters.offerUuid, t],
  );

  const columnList = useMemo(
    () => offerPurchaseColumns({ t, projectSlug, canLink }),
    [t, projectSlug, canLink],
  );

  const getRowHref = useCallback(
    (row: OfferPurchaseRow): string | undefined =>
      getOfferPurchaseHref(row, projectSlug, canLink),
    [canLink, projectSlug],
  );

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("offersPurchasesTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          defaultSorting={{ id: "total_sales", desc: true }}
          getRowHref={getRowHref}
          isError={isError}
          isLoading={isLoading || isPreviousData}
          onRetry={refetch}
          urlSync={{ prefix: "purchases" }}
          resetPageOn={[
            filters.startDate.valueOf(),
            filters.endDate.valueOf(),
            filters.offerUuid,
          ]}
        />
      </CardContent>
    </Card>
  );
}

export default PurchasesTable;
